import { useMemo } from 'react';
import { DataTable } from 'components/data-table';
import type {
  DataTableColumnDef,
  DataTableSelectionState,
} from 'components/data-table/types';
import { format0 } from 'utils/number';
import type { LtdCandidate } from './composite-geometry';

interface Row extends Record<string, unknown> {
  id: string;
  ltdPropertyId: number;
  name: string;
  formation: string;
  unitSurveyArea: string;
  typeCurveArea: string;
  perforatedLateralLengthFt: string;
}

const EMPTY = '—';

const COLUMNS: DataTableColumnDef<Row>[] = [
  { accessorKey: 'ltdPropertyId', header: 'Property ID' },
  { accessorKey: 'name', header: 'Name' },
  { accessorKey: 'formation', header: 'Formation' },
  { accessorKey: 'unitSurveyArea', header: 'Unit / Survey Area' },
  { accessorKey: 'typeCurveArea', header: 'Type Curve Area' },
  {
    accessorKey: 'perforatedLateralLengthFt',
    header: 'Perforated Lateral (ft)',
  },
];

/**
 * The candidates as table rows, with every blank rendered as an em dash.
 * @param {LtdCandidate[]} candidates The candidates on this page.
 * @return {Row[]} The rows.
 */
const toRows = (candidates: LtdCandidate[]): Row[] =>
  candidates.map((candidate) => ({
    id: String(candidate.ltdPropertyId),
    ltdPropertyId: candidate.ltdPropertyId,
    name: candidate.name ?? EMPTY,
    formation: candidate.formation ?? EMPTY,
    unitSurveyArea: candidate.unitSurveyArea ?? EMPTY,
    typeCurveArea: candidate.typeCurveArea ?? EMPTY,
    // The PERFORATED column, not the geometric one — it is the figure the save reports, so
    // showing it here is what lets a reader reconcile the row against the sheet's total.
    perforatedLateralLengthFt:
      candidate.perforatedLateralLengthFt === null
        ? EMPTY
        : format0(candidate.perforatedLateralLengthFt),
  }));

interface LtdSelectionTableProps {
  candidates: LtdCandidate[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  /** Slot ids checked across every page, not just this one. */
  selectedIds: Set<number>;
  onToggle: (ids: number[], selected: boolean) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  onSearchChange: (text: string) => void;
}

/**
 * The sheet's LTD picker.
 *
 * Server-paged and server-searched: one page is all the browser holds, so narrowing happens
 * in SQL. The selection spans pages, which is what makes a composite out of sticks that do
 * not sit on the same page — so the table's controlled selection is projected from the
 * caller's Set onto the rows CURRENTLY on screen, and a toggle reconciles only those. This
 * is the same shape `create-workspace-modal` uses; rebuilding the Set from one page would
 * silently drop every stick picked on a page navigated away from.
 * @param {LtdSelectionTableProps} props The rows, the paging, the selection and the handlers.
 * @return {JSX.Element} The table.
 */
export function LtdSelectionTable({
  candidates,
  totalCount,
  loading,
  error,
  selectedIds,
  onToggle,
  page,
  pageSize,
  onPageChange,
  onSearchChange,
}: LtdSelectionTableProps) {
  const rows = useMemo(() => toRows(candidates), [candidates]);

  const selection = useMemo<DataTableSelectionState>(() => {
    const onPage = rows.filter((row) => selectedIds.has(row.ltdPropertyId));
    const allOnPage = rows.length > 0 && onPage.length === rows.length;

    return allOnPage
      ? { selectAll: true, toggledNodes: [] }
      : { selectAll: false, toggledNodes: onPage.map((row) => row.id) };
  }, [rows, selectedIds]);

  /**
   * Turns the table's selection state into the add/remove pair the parent wants.
   * @param {DataTableSelectionState} next What the table now has selected.
   */
  const handleSelectionChange = (next: DataTableSelectionState) => {
    const toggled = new Set(next.toggledNodes);
    /**
     * @param {string} id A row id.
     * @return {boolean} Whether that row is selected.
     */
    const isSelected = (id: string) => {
      return next.selectAll ? !toggled.has(id) : toggled.has(id);
    };

    const add: number[] = [];
    const remove: number[] = [];
    rows.forEach((row) => {
      (isSelected(row.id) ? add : remove).push(row.ltdPropertyId);
    });

    if (add.length) onToggle(add, true);
    if (remove.length) onToggle(remove, false);
  };

  return (
    <DataTable<Row>
      data={rows}
      columns={COLUMNS}
      getRowId={(row) => row.id}
      isLoading={loading}
      error={error}
      initialTableState={{ page, pageSize }}
      scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
      controlledState={{ selection }}
      onSelectionChange={(state) => handleSelectionChange(state.selection)}
      onPaginationChange={(state) => onPageChange(state.page, state.pageSize)}
      onGlobalSearchChange={(state) => onSearchChange(state.globalFilter)}
      searchPlaceholder="Search Property ID or name…"
      config={{
        selectionType: 'multi',
        // The endpoint orders by id and takes no sort, so offering one would lie.
        sorting: false,
        globalSearch: true,
        columnFilters: false,
        columnVisibility: false,
        columnReordering: false,
        columnResizing: false,
        columnPinning: false,
        export: false,
      }}
    />
  );
}
